<template>
  <d-card :class="{ 'mb-3': true, 'is-deleted': productData.deleted }">
    <d-card-body class="pb-2">
      <d-row>
        <d-col
          class="px-0"
          cols="5">
          <div>
            {{ productName }}<br>
            <small>
              Ilość: <span class="text-primary">{{ productData.quantity }} szt.</span> |
              Kolor: <span class="text-primary">{{ productColor }}</span>
              <template v-if="productMaterial !== 'Brak'">
                | Materiał: <span class="text-primary">{{ productMaterial }}</span><br>
                <strong>Użycie materiału:</strong> <br>
                1 szt.: <span class="text-primary">{{ productData.usage_cm_per_item }} cm<sup>2</sup></span>
                <template v-if="productData.usage_cm">
                  | Wszystkie produkty: <span class="text-primary">{{ productData.usage_cm }} cm<sup>2</sup></span>
                </template>
                <template v-if="productData.usage_bolt">
                  | Zużycie belek: <span class="text-primary">{{ productData.usage_bolt }}</span>
                </template>
              </template>
            </small>
          </div>
        </d-col>

        <d-col
          class="px-0"
          cols="7">
          <div class="d-flex text-right align-items-center">
            <d-col cols="4">
              <div
                v-if="productData.size_name"
                class="text-left py-1">
                Rozmiar: <br>
                <span class="text-primary">{{ productSize }}</span>
              </div>
            </d-col>

            <d-col cols="4">
              <div
                v-if="productData.polar_id"
                class="text-left">
                Polar: <br>
                <span class="text-primary">{{ productPolar }}</span>
              </div>
            </d-col>

            <d-col cols="4">
              <div
                v-if="productData.seam_id"
                class="text-left">
                Przeszycie: <br>
                <span class="text-primary">{{ productSeam }}</span>
              </div>
            </d-col>
          </div>
        </d-col>
      </d-row>

      <d-row v-if="!productData.procedure_start_date">
        <d-col
          cols="12"
          class="px-0 mt-3">
          <d-alert
            show
            theme="warning"
            class="text-center">
            Aby skonfigurować pracę zdalną, należy najpierw przypisać procedurę do produktu i zapisać zamówienie...
          </d-alert>
        </d-col>
      </d-row>

      <d-row v-if="productData.procedure_start_date">
        <hr class="w-100">
        <div class="d-flex w-100">
          <small class="m-0 w-75">
            Wybrana procedura:
            <span class="text-primary">
              {{ procedureName }}
            </span>
            <br>
            Aktywne procesy:
            <template v-for="(process, index) of productData.processes">
              <span
                v-if="index > 0"
                :key="'product-process-separator-' + index"
                class="px-1">
                &#8226;
              </span>
              <span
                :key="'product-process-' + index"
                class="text-primary">
                {{ process.name }}
              </span>
            </template>
            <br>
            <span
              v-if="productData.pattern_required"
              class="text-success">
              Wymaga wykroju
            </span>
            <span
              v-else
              class="text-danger">
              Nie wymaga wykroju
            </span>
            <span class="px-1">&#8226;</span>
            <span
              v-if="productData.pattern_before_order"
              class="text-success">
              Z wykrajaniem przed zamówieniem
            </span>
            <span
              v-else
              class="text-danger">
              Bez wykrajania przed zamówieniem
            </span>
          </small>
        </div>
      </d-row>

      <d-row
        v-if="productData.procedure_start_date"
        class="mt-4">
        <table class="table table-sm">
          <thead>
            <tr>
              <th>Przewoźnik</th>
              <th>Wykonawca</th>
              <th>Liczba sztuk</th>
              <th>Powiązane procesy</th>
              <th>Blokada procesu</th>
              <th>Terminy</th>
              <th>Akcje</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(job, index) of filteredRemoteJobs"
              :key="'job-item-' + index">
              <td>
                <small>
                  Dostawca: <span class="text-primary">{{ job.delivery_driver.name }}</span><br>
                  Odbierający: <span class="text-primary">{{ job.collection_driver.name }}</span>
                </small>
              </td>
              <td>
                <small>
                  {{ job.worker.name }}
                </small>
              </td>
              <td>
                <small>
                  Do wykonania:
                  <strong>
                    {{ job.quantity_to_execution }} z {{ productData.quantity }} szt.
                  </strong>
                </small><br>
                <small>
                  Odebrano:
                  <span
                    v-if="job.quantity_collected === job.quantity_to_execution"
                    class="text-success">
                    {{ job.quantity_collected }} z {{ job.quantity_to_execution }} szt.
                  </span>
                  <span v-else>
                    {{ job.quantity_collected }} z {{ job.quantity_to_execution }} szt.
                  </span>
                </small>
              </td>
              <td>
                <small>
                  Dostawa przed: <span class="text-primary">{{ job.delivery_order_process.name }}</span><br>
                  Odbiór po: <span class="text-primary">{{ job.collection_order_process.name }}</span>
                </small>
              </td>
              <td>
                <small>
                  <span
                    v-if="job.delivery_block_process"
                    class="text-danger">
                    Dostawa blokuje
                  </span>
                  <span
                    v-else
                    class="text-success">
                    Dostawa nie blokuje
                  </span>
                  <br>
                  <span
                    v-if="job.collection_block_process"
                    class="text-danger">
                    Odbiór blokuje
                  </span>
                  <span
                    v-else
                    class="text-success">
                    Odbiór nie blokuje
                  </span>
                </small>
              </td>
              <td>
                <small>
                  Dostawa: <span class="text-primary">{{ job.planned_delivery_date }}</span><br>
                  Odbiór: <span class="text-primary">{{ job.planned_collection_date }}</span>
                </small>
              </td>
              <td>
                <d-button
                  theme="primary"
                  size="sm"
                  @click.prevent="editRemoteJob(job)">
                  <i class="material-icons">edit</i>
                </d-button>
                <d-button
                  theme="danger"
                  size="sm"
                  class="ml-2"
                  :disabled="removeInProgress || !!job.collected_at || !!job.delivered_at"
                  :title="!!job.collected_at || !!job.delivered_at ? 'Nie można usunąć pracy zdalnej, która została dostarczona/odebrana' : 'Usuń pracę zdalną'"
                  @click.prevent="removeRemoteJob(job.id)">
                  <i class="material-icons">delete</i>
                </d-button>
              </td>
            </tr>
          </tbody>
        </table>
      </d-row>
    </d-card-body>
  </d-card>
</template>

<script>
// import Vue from 'vue';
// import FormUtils from './../../../utils/FormUtils.js';
import ListUtils from './../../../utils/ListUtils.js';

export default {
  name: 'remote-job-product',
  props: [
    'orderID',
    'productData',
    'productIndex',
    'products',
    'procedures',
    'materials',
    'colors',
    'seams',
    'strands',
    'sizes',
    'polars',
    'workers',
    'remoteJobs',
    'canEdit'
  ],
  computed: {
    filteredRemoteJobs () {
      if (!this.remoteJobs) {
        return [];
      }

      return this.remoteJobs.filter(job => job.order_product_id === this.productData.id);
    },
    productApiData () {
      let foundedProduct = this.products.filter(product => product.id === this.productData.product_id);

      if (foundedProduct[0]) {
        return foundedProduct[0];
      }

      return false;
    },
    productName () {
      if (this.productApiData) {
        return this.productApiData.name;
      }

      if (this.productData.product_name) {
        return this.productData.product_name;
      }

      return '---';
    },
    productsWithAllColorsAndSizes () {
      return this.products.filter(product => product.price_code === product.code).map(product => product.id);
    },
    isProductWithAllColorsAndSizes () {
      return this.productsWithAllColorsAndSizes.indexOf(this.productData.product_id) > -1;
    },
    productMaterial () {
      if (!this.productData.color_id && !this.productData.color_name) {
        return 'Brak';
      }

      if (this.productData.material_name) {
        return this.productData.material_name;
      }

      if (this.productData.color_id) {
        let foundedColor = this.colors.filter(color => color.id === this.productData.color_id);

        if (foundedColor[0]) {
          let materialID = foundedColor[0].material_id;
          let foundedMaterial = this.materials.filter(material => material.id === materialID);

          if (foundedMaterial[0]) {
            return foundedMaterial[0].name;
          }
        }
      }

      return '???';
    },
    productColor () {
      if (this.productData.color_id && this.productData.id === null) {
        let foundedColor = this.colors.filter(color => color.id === this.productData.color_id);

        if (foundedColor[0]) {
          return foundedColor[0].name;
        }

        return '???';
      }

      if (!this.productData.color_id && this.productData.id === null && this.productApiData && this.productApiData.color_name) {
        return this.productApiData.color_name;
      }

      if (this.productData && this.productData.color_name) {
        return this.productData.color_name;
      }

      return 'Brak';
    },
    productSize () {
      if (this.productData && this.productData.size_name) {
        return this.productData.size_name;
      }

      if (this.productData.size_id) {
        let foundedSize = this.sizes.filter(size => size.id === this.productData.size_id);

        if (foundedSize[0]) {
          return foundedSize[0].name;
        }
      }

      return 'Brak';
    },
    productPolar () {
      if (this.productData && this.productData.polar_name) {
        return this.productData.polar_name;
      }

      if (this.productData.polar_id) {
        let foundedPolar = this.polars.filter(size => size.id === this.productData.polar_id);

        if (foundedPolar[0]) {
          return foundedPolar[0].name;
        }
      }

      return 'Brak';
    },
    productSeam () {
      if (this.productData && this.productData.seam_name) {
        return this.productData.seam_name;
      }

      if (this.productData.seam_id) {
        let foundedSeam = this.seams.filter(size => size.id === this.productData.seam_id);

        if (foundedSeam[0]) {
          return foundedSeam[0].name;
        }
      }

      return 'Brak';
    },
    availableSizes () {
      if (this.isProductWithAllColorsAndSizes) {
        return this.sizes.map(size => ({
          id: size.id,
          name: size.name
        }));
      }

      if (this.productApiData) {
        let sizes = JSON.parse(JSON.stringify(this.productApiData.sizes));

        if (sizes.length) {
          sizes = sizes.filter(size => !!size).map(size => ({
            id: size.id,
            name: size.name
          }));
        }

        return sizes;
      }

      return [];
    },
    procedureIsAssigned () {
      return this.productData.procedure_is_assigned || this.productData.procedure_start_date;
    },
    assignedProcedureData () {
      let procedureID = this.productData.procedure_id;
      let foundedProcedure = this.procedures.filter(procedure => procedure.id === procedureID);

      if (foundedProcedure[0]) {
        return foundedProcedure[0];
      }

      return false;
    },
    procedureName () {
      if (this.assignedProcedureData) {
        return this.assignedProcedureData.name;
      }

      return '---';
    }
  },
  data () {
    return {
      removeInProgress: false
    };
  },
  methods: {
    editRemoteJob (job) {
      this.$bus.$emit('remote-job-popup-show', {
        job_id: job.id,
        order_id: this.orderID,
        order_product_id: this.productData.id,
        procedure_items: this.productData.processes.map(process => ({
          id: process.id,
          name: process.name
        })),
        product_qty: this.productData.quantity,
        delivery_driver_user_id: job.delivery_driver_user_id,
        collection_driver_user_id: job.collection_driver_user_id,
        worker_user_id: job.worker_user_id,
        quantity_to_execution: job.quantity_to_execution,
        delivery_before_order_process_id: job.delivery_before_order_process_id,
        collection_after_order_process_id: job.collection_after_order_process_id,
        delivery_block_process: !!job.delivery_block_process,
        collection_block_process: !!job.collection_block_process,
        planned_delivery_date: job.planned_delivery_date,
        planned_collection_date: job.planned_collection_date,
        delivered_at: job.delivered_at,
        collected_at: job.collected_at
      });
    },
    removeRemoteJob (jobID) {
      this.removeInProgress = true;

      ListUtils.removeItem(this, {
        endpointBase: '/orders/remote-jobs/delete/',
        id: jobID,
        successTitle: 'Usunięto pracę zdalną',
        successText: 'Wybrana praca zdalna została usunięta',
        confirmTitle: 'Czy na pewno chcesz usunąć tę pracę zdalną?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie pracy zdalej nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.removeInProgress = false;
          this.$bus.$emit('order-reload-remote-jobs');
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    }
  }
}
</script>

<style lang="scss">
.is-deleted {
  pointer-events: none;

  & > * {
    opacity: .5;
  }

  &:before {
    background: #d22;
    border-radius: 10px 10px 0 0;
    color: #fff;
    content: "Produkt usunięty";
    height: 24px;
    font-size: 12px;
    line-height: 25px;
    position: absolute;
    text-align: center;
    width: 100%;
  }
}

.order-creator {
  tr.is-overdue {
    background: rgba(255, 50, 50, .075);
  }
}
</style>
